'use client';

import type { Address } from 'viem';
import type { LiquidityPosition } from '@/hooks/liquidity/useLiquidityPositions';
import type { ChainType } from '@/constants/chain-specific';

const STORAGE_KEY_PREFIX = 'lp-positions';
const STORAGE_VERSION = 1;

/**
 * Interface for persisted position data
 */
interface PersistedPositionsData {
    version: number;
    chainId: ChainType;
    positions: LiquidityPosition[];
    lastUpdated: number;
}

/**
 * Interface for all chains' data stored for a user
 */
interface UserPositionsStore {
    [chainId: number]: PersistedPositionsData;
}

/**
 * Get the storage key for a user's positions
 */
function getStorageKey(userAddress: Address): string {
    // Use first 10 chars of address for shorter key
    const shortAddress = userAddress.slice(0, 10).toLowerCase();
    return `${STORAGE_KEY_PREFIX}-v${STORAGE_VERSION}-${shortAddress}`;
}

/**
 * Load all cross-chain positions for a user from localStorage
 */
export function loadUserPositions(userAddress: Address): UserPositionsStore {
    if (typeof window === 'undefined') return {};
    
    try {
        const key = getStorageKey(userAddress);
        const stored = localStorage.getItem(key);
        
        if (!stored) return {};
        
        const data = JSON.parse(stored) as UserPositionsStore;
        return data;
    } catch (error) {
        console.error('[CrossChainPositions] Error loading positions:', error);
        return {};
    }
}

/**
 * Save positions for a specific chain
 */
export function saveChainPositions(
    userAddress: Address,
    chainId: ChainType,
    positions: LiquidityPosition[]
): void {
    if (typeof window === 'undefined') return;
    
    try {
        const key = getStorageKey(userAddress);
        const existing = loadUserPositions(userAddress);
        
        const updated: UserPositionsStore = {
            ...existing,
            [chainId]: {
                version: STORAGE_VERSION,
                chainId,
                positions,
                lastUpdated: Date.now(),
            },
        };
        
        localStorage.setItem(key, JSON.stringify(updated));
        console.log(`[CrossChainPositions] Saved ${positions.length} positions for chain ${chainId}`);
    } catch (error) {
        console.error('[CrossChainPositions] Error saving positions:', error);
    }
}

/**
 * Get positions for a specific chain
 */
export function getChainPositions(
    userAddress: Address,
    chainId: ChainType
): LiquidityPosition[] {
    const store = loadUserPositions(userAddress);
    return store[chainId]?.positions || [];
}

/**
 * Get all chains that have persisted positions for a user
 */
export function getChainsWithPositions(userAddress: Address): ChainType[] {
    const store = loadUserPositions(userAddress);
    return Object.keys(store).map(Number) as ChainType[];
}

/**
 * Get positions for multiple chains
 */
export function getMultiChainPositions(
    userAddress: Address,
    chainIds: ChainType[]
): { chainId: ChainType; positions: LiquidityPosition[] }[] {
    const store = loadUserPositions(userAddress);
    
    return chainIds
        .filter((chainId) => store[chainId]?.positions?.length > 0)
        .map((chainId) => ({
            chainId,
            positions: store[chainId].positions,
        }));
}

/**
 * Clear positions for a specific chain
 */
export function clearChainPositions(userAddress: Address, chainId: ChainType): void {
    if (typeof window === 'undefined') return;
    
    try {
        const key = getStorageKey(userAddress);
        const existing = loadUserPositions(userAddress);
        
        if (existing[chainId]) {
            delete existing[chainId];
            localStorage.setItem(key, JSON.stringify(existing));
            console.log(`[CrossChainPositions] Cleared positions for chain ${chainId}`);
        }
    } catch (error) {
        console.error('[CrossChainPositions] Error clearing positions:', error);
    }
}

/**
 * Clear all positions for a user
 */
export function clearAllPositions(userAddress: Address): void {
    if (typeof window === 'undefined') return;
    
    try {
        const key = getStorageKey(userAddress);
        localStorage.removeItem(key);
        console.log('[CrossChainPositions] Cleared all positions');
    } catch (error) {
        console.error('[CrossChainPositions] Error clearing all positions:', error);
    }
}

/**
 * Get the last updated timestamp for a chain's positions
 */
export function getLastUpdated(userAddress: Address, chainId: ChainType): number | null {
    const store = loadUserPositions(userAddress);
    return store[chainId]?.lastUpdated || null;
}

/**
 * Get summary stats for cross-chain positions
 */
export function getCrossChainStats(userAddress: Address): {
    totalPositions: number;
    totalValueUsd: number;
    chainCount: number;
    chains: { chainId: ChainType; positionCount: number; totalValue: number }[];
} {
    const store = loadUserPositions(userAddress);
    
    const chains = Object.entries(store).map(([chainIdStr, data]) => {
        const chainId = Number(chainIdStr) as ChainType;
        const positions = data.positions || [];
        
        return {
            chainId,
            positionCount: positions.length,
            totalValue: positions.reduce((sum: number, p: LiquidityPosition) => sum + (p.totalValueUsd || 0), 0),
        };
    });
    
    return {
        totalPositions: chains.reduce((sum: number, c) => sum + c.positionCount, 0),
        totalValueUsd: chains.reduce((sum: number, c) => sum + c.totalValue, 0),
        chainCount: chains.length,
        chains,
    };
}
